import React, { useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import usePlanningInstructorsData from '../hooks/usePlanningInstructorsData';
import InstructorUserDropdown from '../../components/InstructorUserDropdown';

/**
 * Planlama detayındaki eğitmen ataması. `InstructorsTab.jsx` ile aynı desen, `planningId`
 * bazında çalışır. Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 2.md §9.
 */
const PlanningInstructorsTab = ({ planningId, readOnly = false }) => {
    const { th } = useLanguage();
    const { instructorsApi, isLoading, isAdding, isDeleting, addInstructor, removeInstructor } = usePlanningInstructorsData(planningId);
    const [selectedUserId, setSelectedUserId] = useState(null);

    const instructors = useMemo(() => instructorsApi?.items || [], [instructorsApi]);
    const assignedUserIds = useMemo(() => instructors.map((row) => row.userId), [instructors]);

    const handleAdd = async () => {
        if (!selectedUserId) return;
        const success = await addInstructor(selectedUserId);
        if (success) setSelectedUserId(null);
    };

    const handleRemove = (row) => {
        confirmDialog({
            message: th('trainingPlanning.instructors.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => removeInstructor(row.id)
        });
    };

    const actionBodyTemplate = (row) => <i className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700" style={{ fontSize: '1.1rem' }} title={th('common.delete')} onClick={() => handleRemove(row)} />;

    return (
        <div>
            <ConfirmDialog />
            {!readOnly && (
                <div className="flex align-items-end gap-2 mb-3">
                    <div style={{ minWidth: '20rem' }}>
                        <label className="block mb-1">{th('trainingPlanning.instructors.selectUser')}</label>
                        <InstructorUserDropdown value={selectedUserId} onChange={setSelectedUserId} excludeUserIds={assignedUserIds} placeholder={th('trainingPlanning.instructors.selectUserPlaceholder')} className="w-full" />
                    </div>
                    <Button label={th('trainingPlanning.instructors.addButton')} icon="pi pi-plus" onClick={handleAdd} disabled={!selectedUserId} loading={isAdding} />
                </div>
            )}

            <DataTable value={instructors} loading={isLoading || isDeleting} dataKey="id" emptyMessage={th('trainingPlanning.instructors.emptyMessage')} className="p-datatable-gridlines" showGridlines>
                {!readOnly && <Column body={actionBodyTemplate} style={{ width: '4rem' }} align="center" />}
                <Column field="userId" header={th('trainingPlanning.instructors.columnUserId')} style={{ width: '8rem' }} />
                <Column field="userFullName" header={th('trainingPlanning.instructors.columnUserFullName')} />
                <Column field="userEmail" header={th('trainingPlanning.instructors.columnUserEmail')} />
                <Column field="createdDate" header={th('trainingPlanning.instructors.columnAssignedDate')} style={{ width: '10rem' }} body={(row) => dateOnlyBodyTemplate(row, 'createdDate')} />
            </DataTable>
        </div>
    );
};

export default PlanningInstructorsTab;
